import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "@/components/SEO";
import KineticHero from "@/components/KineticHero";
import AffiliateGearCard from "@/components/AffiliateGearCard";
import NewsletterSection from "@/components/NewsletterSection";
import EditorialMarquee from "@/components/EditorialMarquee";
import { Reveal, Eyebrow } from "@/components/Motion";
import { TIPS_FLY, HATCH_GUIDE } from "@/data/checklists";
import { GEAR } from "@/data/gear";

const HERO_IMG = "/hero-fly-creek.webp";

const ROD_SELECTOR = [
    {
        id: "small-creeks",
        label: "Small Creeks",
        rec: "7'6\"–8ft 3wt",
        line: "Weight-forward floating, light presentation",
        leader: "7.5ft 5X tapered leader",
        why: "Short, soft, and accurate under overhanging brush. A 3wt turns a 20-foot bow-and-arrow cast into a weapon.",
    },
    {
        id: "freestone",
        label: "Freestone Rivers",
        rec: "9ft 5wt",
        line: "Weight-forward floating, all-around taper",
        leader: "9ft 4X tapered leader",
        why: "The universal trout tool. Enough backbone for wind and streamers, delicate enough for size-18 dries.",
    },
    {
        id: "tailwaters",
        label: "Big Tailwaters",
        rec: "9'6\"–10ft 5–6wt",
        line: "Long-belly floating or Euro nymph line",
        leader: "12ft 5X–6X for spooky fish",
        why: "Extra reach for mending across seams and staying off educated fish that have seen every drift.",
    },
    {
        id: "stillwater",
        label: "Lakes & Stillwater",
        rec: "9'6\" 6–7wt",
        line: "Floating + intermediate sinking spare spool",
        leader: "9ft 3X–4X for leeches and buggers",
        why: "Distance and depth control. A 6wt punches through lake wind and turns over big streamers all day.",
    },
];

export default function FlyFishing() {
    const [water, setWater] = useState(ROD_SELECTOR[1]);
    const flyGear = GEAR.filter((g) => ["Fly Rods", "Flies"].includes(g.category));

    return (
        <>
            <SEO
                title="Fly Fishing Guide — Hatches, Drifts & Rod Selection"
                description="Learn to read trout water, match the hatch through all four stages, pick the right fly rod for any river, and fish six field-tested rules that put bends in rods."
            />
            <KineticHero
                bright
                image={HERO_IMG}
                eyebrow="The Quiet Art — Fly Fishing"
                lines={[
                    { text: "The drift is" },
                    { text: "everything.", accent: "teal" },
                ]}
                sub="Match the hatch, master the mend, and learn why ten feet of controlled line beats fifty feet of hope."
            >
                <a
                    href="#fly-tips"
                    data-testid="fly-hero-cta-tips"
                    onClick={(e) => {
                        e.preventDefault();
                        window.__lenis?.scrollTo("#fly-tips", { offset: -80 });
                    }}
                    className="rounded-full bg-teal px-7 py-3.5 font-sans text-sm font-semibold text-[#0B1320] hover:bg-coral hover:text-white transition-colors duration-300"
                >
                    Read the Field Guide
                </a>
            </KineticHero>

            <EditorialMarquee items={["Dead Drift", "Match the Hatch", "Reach Mend", "Pocket Water", "Tight Line", "Rise Forms"]} />

            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="hatch-guide-section">
                <Reveal>
                    <Eyebrow>Anatomy of a Hatch</Eyebrow>
                    <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-100 max-w-3xl">
                        Four stages. Four opportunities. One lifetime to learn them.
                    </h2>
                </Reveal>
                <div className="mt-12 space-y-px rounded-2xl overflow-hidden border border-white/10">
                    {HATCH_GUIDE.map((h, i) => (
                        <Reveal key={h.stage} delay={i * 0.06}>
                            <div className="group grid sm:grid-cols-[180px_1fr_220px_1fr] gap-4 bg-[#111C2E] p-6 sm:p-8 hover:bg-[#1A273D] transition-colors duration-500 items-start">
                                <div className="flex items-baseline gap-3">
                                    <span className="font-serif text-3xl font-light text-teal/40">0{i + 1}</span>
                                    <span className="font-sans text-lg font-medium text-slate-100">{h.stage}</span>
                                </div>
                                <p className="font-body text-sm text-slate-400">{h.when}</p>
                                <p className="font-mono text-xs sm:text-sm text-teal">{h.pattern}</p>
                                <p className="font-body text-sm text-slate-300">{h.tactic}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-[#0D1626] border-y border-white/10 py-24 sm:py-32" data-testid="rod-selector-section">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <Eyebrow>Rod, Line & Leader Selector</Eyebrow>
                        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-100">
                            Tell us the water. We'll rig the rod.
                        </h2>
                    </Reveal>

                    <div className="mt-10 flex flex-wrap gap-3">
                        {ROD_SELECTOR.map((w) => (
                            <button
                                key={w.id}
                                onClick={() => setWater(w)}
                                data-testid={`rod-selector-${w.id}`}
                                className={`rounded-full px-5 py-2.5 font-sans text-sm font-medium transition-colors duration-300 ${
                                    water.id === w.id
                                        ? "bg-teal text-[#0B1320]"
                                        : "border border-white/15 text-slate-300 hover:border-teal/50 hover:text-teal"
                                }`}
                            >
                                {w.label}
                            </button>
                        ))}
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={water.id}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-8 rounded-2xl border border-teal/25 bg-[#111C2E] p-8 sm:p-10 grid sm:grid-cols-3 gap-8"
                            data-testid="rod-selector-result"
                        >
                            <div>
                                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-slate-500">Rod</p>
                                <p className="mt-2 font-serif text-2xl sm:text-3xl font-semibold text-teal">{water.rec}</p>
                            </div>
                            <div>
                                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-slate-500">Line</p>
                                <p className="mt-2 font-body text-base text-slate-200">{water.line}</p>
                                <p className="mt-4 font-mono text-[10px] tracking-[0.28em] uppercase text-slate-500">Leader</p>
                                <p className="mt-2 font-body text-base text-slate-200">{water.leader}</p>
                            </div>
                            <div>
                                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-slate-500">Why it works</p>
                                <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{water.why}</p>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>

            <section id="fly-tips" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="fly-tips-section">
                <Reveal>
                    <Eyebrow>Field Notes — Six Rules</Eyebrow>
                    <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-100">
                        Tips that put bends in rods
                    </h2>
                </Reveal>
                <div className="mt-12 grid md:grid-cols-2 gap-6">
                    {TIPS_FLY.map((t, i) => (
                        <Reveal key={t.num} delay={(i % 2) * 0.1}>
                            <div className="group h-full rounded-2xl border border-white/10 bg-[#111C2E] p-8 hover:border-teal/40 transition-colors duration-500">
                                <span className="font-serif text-5xl font-light text-teal/20 group-hover:text-teal/40 transition-colors duration-500">{t.num}</span>
                                <h3 className="mt-3 font-sans text-xl font-medium text-slate-100">{t.title}</h3>
                                <p className="mt-3 font-body text-sm leading-relaxed text-slate-400">{t.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32" data-testid="fly-gear-section">
                <Reveal>
                    <Eyebrow>Fly Kit We Trust</Eyebrow>
                    <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-100">
                        Rods and flies that earn their keep
                    </h2>
                </Reveal>
                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {flyGear.map((item, i) => (
                        <AffiliateGearCard key={item.id} item={item} index={i} />
                    ))}
                </div>
            </section>

            <NewsletterSection />
        </>
    );
}
